import { makeStyles, Box, Typography } from "@material-ui/core";
import * as React from "react";

import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import {
  getFormatCost,
  getFormatStartMonth,
  getLatestMonthlyCashflowResult,
  getLatestMonthlyCashflowResultByCostType,
} from "@/utilities/propertyEdit";
import { PropertyHolder } from "@/view_models/atoms";

import { LatestHistoryContent } from "../../EditPropertyHistory";

import { EditPropertySectionManagementCostHistory } from "./History";
import { ManagementCostType } from "./types";

const useStyles = makeStyles(() => ({
  content: {
    maxWidth: 640,
    margin: "0 auto",
  },
}));

interface EditPropertySectionManagementCostProps {
  propertyHolder: PropertyHolder;
  onClickPageBack: () => void;
}

export const EditPropertySectionManagementCost: React.FC<EditPropertySectionManagementCostProps> = ({
  propertyHolder,
  onClickPageBack,
}) => {
  const classes = useStyles();
  // 過去履歴の表示・非表示
  const [isShowHistory, setIsShowHistory] = React.useState(false);
  // 過去履歴ページに渡す情報
  const [selectedCostType, setSelectedCostType] = React.useState<ManagementCostType | null>(null);

  /*
  　各種コストの取得
  */
  // management_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultManagementCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "management_cost");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const managementCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultManagementCost?.management_cost),
    [latestMonthlyCashflowResultManagementCost]
  );

  // building_management_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultBuildingManagementCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(
      propertyHolder.user_input_monthly_cashflow_results,
      "building_management_cost"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const buildingManagementCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultBuildingManagementCost?.building_management_cost),
    [latestMonthlyCashflowResultBuildingManagementCost]
  );

  // repair_fund_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultRepairFundCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "repair_fund_cost");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const repairFundCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultRepairFundCost?.repair_fund_cost),
    [latestMonthlyCashflowResultRepairFundCost]
  );

  // communication_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultCommunicationCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "communication_cost");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const communicationCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultCommunicationCost?.communication_cost),
    [latestMonthlyCashflowResultCommunicationCost]
  );

  // misc_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultMiscCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "misc_cost");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const miscCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultMiscCost?.misc_cost),
    [latestMonthlyCashflowResultMiscCost]
  );

  // property_tax を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultPropertyTax = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "固定資産税・都市計画税"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const propertyTax = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultPropertyTax?.property_tax),
    [latestMonthlyCashflowResultPropertyTax]
  );

  // supplies_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultSuppliesCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResult(propertyHolder.user_input_monthly_cashflow_results, "supplies_cost");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const suppliesCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultSuppliesCost?.supplies_cost),
    [latestMonthlyCashflowResultSuppliesCost]
  );

  // large_scale_repair_fund_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultLargeScaleRepairFundCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "設備修繕、大規模修繕費用"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const largeScaleRepairFundCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultLargeScaleRepairFundCost?.large_scale_repair_fund_cost),
    [latestMonthlyCashflowResultLargeScaleRepairFundCost]
  );

  // misc_onetime_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultMiscOnetimeCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "その他一過性の支出"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const miscOnetimeCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultMiscOnetimeCost?.misc_onetime_cost),
    [latestMonthlyCashflowResultMiscOnetimeCost]
  );

  const onClickPageBackFromHistory = () => {
    setIsShowHistory(false);
  };
  if (isShowHistory) {
    return (
      <EditPropertySectionManagementCostHistory
        costType={selectedCostType}
        onClickPageBack={onClickPageBackFromHistory}
      />
    );
  }

  const handleClickShowMoreHistory = (costType: ManagementCostType) => {
    setSelectedCostType(costType);
    setIsShowHistory(true);
  };

  return (
    <BaseTemplate headerTitle="経費情報" onBack={onClickPageBack} isBackGroundWhite={true}>
      <Box>
        <Box className={classes.content}>
          <Typography component="h3" data-edit-section>
            毎月かかる経費
          </Typography>
          {/* 建物管理費 */}
          <LatestHistoryContent
            title="建物管理費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultBuildingManagementCost) },
              { label: "金額", value: buildingManagementCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("建物管理費")}
          />

          {/* 賃貸管理費 */}
          <LatestHistoryContent
            title="賃貸管理費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultManagementCost) },
              { label: "金額", value: managementCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("賃貸管理費")}
          />

          {/* 修繕積立金 */}
          <LatestHistoryContent
            title="修繕積立金"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultRepairFundCost) },
              { label: "金額", value: repairFundCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("修繕積立金")}
          />

          {/* 通信費 */}
          <LatestHistoryContent
            title="通信費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultCommunicationCost) },
              { label: "金額", value: communicationCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("通信費")}
          />

          {/* その他の経費 */}
          <LatestHistoryContent
            title="その他経費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultMiscCost) },
              { label: "金額", value: miscCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("その他経費")}
          />

          <Typography component="h3" data-edit-section>
            年に一度・不定期の経費
          </Typography>
          {/* 固定資産税・都市計画税 */}
          <LatestHistoryContent
            title="固定資産税・都市計画税"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultPropertyTax) },
              { label: "金額", value: propertyTax },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("固定資産税・都市計画税")}
          />

          {/* 設備修繕、大規模修繕費用 */}
          <LatestHistoryContent
            title="設備修繕、大規模修繕費用"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultLargeScaleRepairFundCost) },
              { label: "金額", value: largeScaleRepairFundCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("設備修繕、大規模修繕費用")}
          />

          {/* 消耗品費 */}
          <LatestHistoryContent
            title="消耗品費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultSuppliesCost) },
              { label: "金額", value: suppliesCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("消耗品費")}
          />

          {/* その他一過性の支出 */}
          <LatestHistoryContent
            title="その他一過性の支出"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultMiscOnetimeCost) },
              { label: "金額", value: miscOnetimeCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("その他一過性の支出")}
          />
        </Box>
      </Box>
    </BaseTemplate>
  );
};
